"use client";

import { useState } from "react";
import { ru } from "@/content/ru";

/**
 * Экран закрытого сайта (отдельный маршрут `/lock`): логотип, название,
 * подпись «хобби-мастерская» и поле для пароля доступа. Пока пароль не введён —
 * ни одна страница сайта недоступна (за это отвечает единый барьер в
 * `proxy.ts`). Доступ хранится в отдельной cookie (через /api/site-lock).
 */
export function LockScreen({ logo, name, tagline }: { logo: string; name: string; tagline: string }) {
  const t = ru.ui.lock;
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await fetch("/api/site-lock", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      if (res.ok) {
        // Cookie установлена — уходим на главную (барьер пропустит)
        window.location.assign("/");
        return;
      }
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      if (res.status === 429) setError(t.errorRate);
      else if (res.status === 401) setError(t.errorWrong);
      else setError(data.error ?? t.errorWrong);
    } catch {
      setError(t.errorNetwork);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="relative flex min-h-dvh flex-col items-center justify-center overflow-hidden px-4">
      {/* Мягкое свечение на фоне */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-1/4 mx-auto h-96 max-w-3xl rounded-full bg-primary/20 blur-3xl dark:bg-primary/15"
      />

      <div className="relative flex w-full max-w-sm flex-col items-center text-center">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={logo} alt={`Логотип ${name}`} width={96} height={96} className="rounded-2xl" />

        <h1 className="mt-6 text-3xl font-bold tracking-tight text-foreground">{name}</h1>
        <p className="mt-2 text-sm font-medium uppercase tracking-[0.2em] text-primary">{tagline}</p>

        <p className="mt-5 text-sm text-muted-foreground">{t.description}</p>

        <form onSubmit={onSubmit} className="mt-8 w-full space-y-3">
          <label htmlFor="lock-password" className="sr-only">
            {t.passwordLabel}
          </label>
          <input
            id="lock-password"
            type="password"
            autoComplete="current-password"
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t.passwordPlaceholder}
            className="w-full rounded-lg border border-input bg-background px-4 py-2.5 text-center text-sm outline-none transition-colors focus:border-ring"
            // Менеджеры паролей (браузерные расширения) дописывают в поле свои
            // атрибуты (например, wfd-id) уже после SSR — это вызывает warning
            // о гидратации. На сам компонент это не влияет, поэтому глушим его.
            suppressHydrationWarning
          />
          {error && <p className="text-sm text-destructive">{error}</p>}
          <button
            type="submit"
            disabled={loading || password.length === 0}
            className="w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {loading ? t.submitting : t.submit}
          </button>
        </form>
      </div>
    </div>
  );
}
